import ReceivedAtDate from '../../../../../../components/ReceivedAtDate';
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone/moment-timezone';

import Table3, { columnHelper } from '../../../../../../containers/Table/Table3';
import { actions, PLOT_DATA_RESULT } from '../actions';
import Strings from '../../../../../../Strings';
import { DATE_MONTH_DAY_YEAR, TIME_FORMAT_12_UPPERCASE } from '../../../../../../constants';
import { formatTimeSpanHHMM } from '../../../../../../utils';
import { LengthUnitConverter, LENGTH_UNITS, roundToDecimal } from '../../../../../../utils/unitConverters';
import { PAGE_LIMIT } from '../../../../../../constants';

export const movementColumns = [
  columnHelper.accessor('date', {
    accessorFn: row => row.timestamp,
    header: Strings.date,
    cell: info => moment.unix(info.getValue()).format(DATE_MONTH_DAY_YEAR),
  }),
  columnHelper.accessor('time', {
    accessorFn: row => row.timestamp,
    header: Strings.time,
    cell: info => moment.unix(info.getValue()).format(TIME_FORMAT_12_UPPERCASE),
  }),
  columnHelper.accessor('burn_calories', {
    header: Strings.calories,
    cell: info => (info.getValue() == null ? '-' : `${Math.round((info.getValue() + Number.EPSILON) * 100) / 100} kcal`),
  }),
  columnHelper.accessor('distance', {
    header: Strings.distance,
    cell: info =>
      info.getValue() == null ? '-' : `${roundToDecimal(LengthUnitConverter(info.getValue(), LENGTH_UNITS.m, LENGTH_UNITS.mi), 2)} MI`,
  }),
  columnHelper.accessor('steps', {
    header: Strings.steps,
    cell: info => info.getValue() ?? '-',
  }),
  columnHelper.accessor('exercise_time', {
    header: Strings.exerciseTime,
    cell: info => (info.getValue() == null ? '-' : formatTimeSpanHHMM(info.getValue())),
  }),
  columnHelper.accessor('exercise_intensity', {
    header: Strings.exerciseIntensity,
    cell: info => info.getValue() ?? '-',
  }),
  columnHelper.accessor('rest_hr', {
    header: Strings.restHR,
    accessorFn: row => (row.rest_hr_available ? `${row.rest_heart_rate} bpm` : '-'),
  }),
];

const deviceMovementColumns = [
  {
    ...movementColumns[0],
    cell: info => (
      <ReceivedAtDate
        measuredAt={info.getValue() ? moment.unix(info.getValue()) : null}
        backendTimestamp={info.row.original.backend_timestamp}
        displayFormat={`${DATE_MONTH_DAY_YEAR} ${TIME_FORMAT_12_UPPERCASE}`}
      >
        {moment.unix(info.getValue()).format(DATE_MONTH_DAY_YEAR)}
      </ReceivedAtDate>
    ),
  },
  ...movementColumns.slice(1),
];

function MovementReport(props) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [dataToShow, setDataToShow] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, total: 0, limit: PAGE_LIMIT });
  const pageRequestRef = React.useRef({ offset: 0, limit: PAGE_LIMIT });

  const getPlotData = React.useCallback(() => {
    setIsLoading(true);

    const { deviceId, startDate, endDate, hubId, kitId, patientId } = props;

    const request = {
      deviceId,
      dataType: 'movement_report',
      startDate,
      endDate,
      hubId,
      kitId,
      patientId,
      limit: pageRequestRef.current.limit,
      offset: pageRequestRef.current.offset,
    };

    props.getPlotData(request).then(resp => {
      if (resp.type === PLOT_DATA_RESULT) {
        const events = resp.response.data;
        setDataToShow(events);
        setPagination({
          offset: pageRequestRef.current.offset,
          total: resp.response.pagination?.total || events.length,
          limit: pageRequestRef.current.limit,
        });
      }
      setIsLoading(false);
    });
  }, [props.deviceId, props.startDate, props.endDate, props.hubId, props.kitId, props.patientId]);

  React.useEffect(() => {
    pageRequestRef.current.offset = 0;
    getPlotData();
  }, [props.deviceId, props.startDate, props.endDate]);

  const onPaginationChange = React.useCallback((newPagination) => {
    pageRequestRef.current.offset = newPagination.pageIndex * pageRequestRef.current.limit;
    getPlotData();
  }, [getPlotData]);

  const onPageSizeChange = React.useCallback((newSize) => {
    pageRequestRef.current.offset = 0;
    pageRequestRef.current.limit = newSize;
    getPlotData();
  }, [getPlotData]);

  return (
    <Table3
      name="movement-report"
      isLoading={isLoading}
      data={dataToShow}
      columns={deviceMovementColumns}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
}

MovementReport.propTypes = {
  eventType: PropTypes.string.isRequired,
  deviceId: PropTypes.string.isRequired,
  patientId: PropTypes.string,
  hubId: PropTypes.string,
  kitId: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  getPlotData: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getPlotData: pageRequest => dispatch(actions.getPlotData(pageRequest)),
});

export default connect(null, mapDispatchToProps)(MovementReport);
